import { useMemo } from 'react';
import { isOnboardingRoutePath } from '../utils/onboardingPaths';
/**
 * M2: complete org signup before rest of the dashboard.
 * Hotfix: removed PostHog feature flag and dev/env overrides — gating is always on.
 * Used by ClerkAuthWrapper; keep logic in sync with org-role-permissions skip and redirect.
 *
 * `resolvedActiveOrgId` is ClerkAuthWrapper's session-first org resolution (orgId ||
 * organization?.id || session?.lastActiveOrganizationId || single-membership fallback).
 * Right after onboarding's setActive() call, Clerk's session claim updates a render or two
 * before `clerkUser.organizationMemberships` reloads — without this, the gate reads a stale
 * empty membership list and bounces the user back into /onboarding after a successful org
 * creation (see PE onboarding-gate-race incident, 2026-07-15).
 */
export function useSignupOnboardingGate(
  pathname,
  routerBasename,
  clerkUser,
  devGateOverride = null,
  resolvedActiveOrgId = '',
) {
  const isOnboardingRoute = isOnboardingRoutePath(pathname, routerBasename);
  const rawHasExistingOrg =
    (clerkUser?.organizationMemberships?.length ?? 0) > 0 || Boolean(resolvedActiveOrgId);
  const rawSignupComplete = clerkUser?.publicMetadata?.signupComplete;
  const hasExistingOrg =
    typeof devGateOverride?.hasExistingOrg === 'boolean'
      ? devGateOverride.hasExistingOrg
      : rawHasExistingOrg;
  const signupComplete =
    typeof devGateOverride?.signupComplete === 'boolean'
      ? devGateOverride.signupComplete
      : rawSignupComplete;
  const enableGating = true;
  const skipOrgPermissionsFetch = useMemo(
    () => enableGating && !hasExistingOrg && signupComplete !== true,
    [enableGating, hasExistingOrg, signupComplete],
  );
  const shouldRedirectToOnboarding = useMemo(
    () =>
      enableGating &&
      signupComplete !== true &&
      !hasExistingOrg &&
      !isOnboardingRoute,
    [enableGating, signupComplete, hasExistingOrg, isOnboardingRoute],
  );
  return {
    enableGating,
    isOnboardingRoute,
    skipOrgPermissionsFetch,
    shouldRedirectToOnboarding,
    signupComplete,
  };
}
